import { useMemo } from 'react';

import { useCurrentPartner, usePartnerParties } from 'services/repositories/partners/partners';

/**
 * The current partner's own GLNs, used to stop a customer inviting themselves as a trade
 * partner. Two sources, both already available to the portal and both checked by the
 * backend too:
 *
 * 1. GS1 identifiers on parties the current partner owns
 * 2. the bill-from identifier in partner settings, which needs no party to exist
 *
 * (2) is taken regardless of its declared domain: that domain records which identifier is in
 * use, not whether a GLN exists, so gating on it would drop real own-GLNs. A non-GLN value
 * there is harmless — it can only ever match an entered value that is itself a valid 13-digit
 * GLN. The backend's FindPartnerOwnGlnsAction does the same, so both sides resolve the same set.
 *
 * `enabled` gates the parties request: TradePartners mounts both invite slide-overs on
 * page load, so an ungated query would fire for every visitor to the page.
 *
 * Returns an empty set while loading or after a failed request, so validation fails open
 * and the backend stays the enforcement boundary.
 */
export const useOwnGlns = ({ enabled }: { enabled: boolean }): Set<string> => {
  const { data: currentPartner } = useCurrentPartner();
  const { data: parties } = usePartnerParties({
    variables: { partnerId: enabled ? currentPartner?.id : undefined },
  });

  const settings = currentPartner?.settings;

  return useMemo(() => {
    const glns = new Set<string>();

    parties?.forEach((party) =>
      party.identifiers?.forEach((identifier) => {
        if (identifier.domain === 'GS1' && identifier.identifier) {
          glns.add(identifier.identifier.trim());
        }
      }),
    );

    if (settings?.billFromAddressIdentifier) {
      glns.add(settings.billFromAddressIdentifier.trim());
    }

    return glns;
  }, [parties, settings]);
};
